import { CertificateRepository, CreateCertificateData, UpdateCertificateData, CertificateFilter, GetCertificateData } from '../repository/certificate.repository';
import { UserRepository } from '../repository/user.repository';

export class CertificateUseCase {
  constructor(
    private certificateRepository: CertificateRepository,
    private userRepository: UserRepository
  ) {
    console.log('[CertificateUseCase] Constructor - Dependencies injected');
  }

  // Create certificate with validation
  async createCertificate(certificateData: CreateCertificateData): Promise<GetCertificateData> {
    console.log('[CertificateUseCase.createCertificate] START - Validating certificate data');
    try {
      // Validate required fields
      if (!certificateData.userId) {
        throw new Error('User ID is required');
      }
      if (!certificateData.courseId) {
        throw new Error('Course ID is required');
      }
      if (!certificateData.issuedBy) {
        throw new Error('Issuer is required');
      }
      if (!certificateData.certificateNumber) {
        throw new Error('Certificate number is required');
      }

      // Validate user exists
      const user = await this.userRepository.getUserById(certificateData.userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Validate certificate number is unique
      const existingCert = await this.certificateRepository.getCertificateByNumber(certificateData.certificateNumber);
      if (existingCert) {
        throw new Error('Certificate number already exists');
      }

      // Validate score percentage if provided
      if (certificateData.scorePercentage !== undefined) {
        if (certificateData.scorePercentage < 0 || certificateData.scorePercentage > 100) {
          throw new Error('Score percentage must be between 0 and 100');
        }
      }

      console.log('[CertificateUseCase.createCertificate] Validation passed, creating certificate');
      const certificate = await this.certificateRepository.createCertificate(certificateData);
      console.log('[CertificateUseCase.createCertificate] SUCCESS - Certificate created with ID:', certificate.id);
      return certificate;
    } catch (error) {
      console.error('[CertificateUseCase.createCertificate] ERROR:', error);
      throw error;
    }
  }

  // Update certificate with validation
  async updateCertificate(certificateId: string, updateData: UpdateCertificateData): Promise<GetCertificateData> {
    console.log('[CertificateUseCase.updateCertificate] START - Validating update data');
    try {
      // Validate certificate exists
      const existingCertificate = await this.certificateRepository.getCertificateById(certificateId);
      if (!existingCertificate) {
        throw new Error('Certificate not found');
      }

      // Validate score percentage if provided
      if (updateData.scorePercentage !== undefined) {
        if (updateData.scorePercentage < 0 || updateData.scorePercentage > 100) {
          throw new Error('Score percentage must be between 0 and 100');
        }
      }

      // Validate expiry date if provided
      if (updateData.expiryDate && updateData.expiryDate <= new Date()) {
        throw new Error('Expiry date must be in the future');
      }

      console.log('[CertificateUseCase.updateCertificate] Validation passed, updating certificate');
      const updatedCertificate = await this.certificateRepository.updateCertificate(certificateId, updateData);
      console.log('[CertificateUseCase.updateCertificate] SUCCESS - Certificate updated');
      return updatedCertificate;
    } catch (error) {
      console.error('[CertificateUseCase.updateCertificate] ERROR:', error);
      throw error;
    }
  }

  // Get certificate by ID with access control
  async getCertificateById(certificateId: string, requestingUserId?: string): Promise<GetCertificateData | null> {
    console.log('[CertificateUseCase.getCertificateById] START - CertificateId:', certificateId);
    try {
      const certificate = await this.certificateRepository.getCertificateById(certificateId);
      
      if (!certificate) {
        console.log('[CertificateUseCase.getCertificateById] Certificate not found');
        return null;
      }

      // Basic access control - users can only see their own certificates unless they're the issuer
      if (requestingUserId && certificate.userId !== requestingUserId && certificate.issuedBy !== requestingUserId) {
        console.log('[CertificateUseCase.getCertificateById] Access denied - not certificate owner or issuer');
        throw new Error('Access denied');
      }

      console.log('[CertificateUseCase.getCertificateById] SUCCESS - Certificate retrieved');
      return certificate;
    } catch (error) {
      console.error('[CertificateUseCase.getCertificateById] ERROR:', error);
      throw error;
    }
  }

  // Get certificates with business logic
  async getCertificates(filter: CertificateFilter = {}, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateUseCase.getCertificates] START - Filter:', filter);
    try {
      // Apply access control - non-admin users can only see their certificates or certificates they issued
      if (requestingUserId && !filter.userId && !filter.issuedBy) {
        // If no specific filters, default to showing certificates for the requesting user
        filter.userId = requestingUserId;
        console.log('[CertificateUseCase.getCertificates] Applied access control - filtering by userId:', requestingUserId);
      }

      const certificates = await this.certificateRepository.getCertificates(filter, limitCount);
      console.log('[CertificateUseCase.getCertificates] SUCCESS - Retrieved', certificates.length, 'certificates');
      return certificates;
    } catch (error) {
      console.error('[CertificateUseCase.getCertificates] ERROR:', error);
      throw error;
    }
  }

  // Delete certificate with validation
  async deleteCertificate(certificateId: string, requestingUserId?: string): Promise<void> {
    console.log('[CertificateUseCase.deleteCertificate] START - CertificateId:', certificateId);
    try {
      // Validate certificate exists and check permissions
      const certificate = await this.certificateRepository.getCertificateById(certificateId);
      if (!certificate) {
        throw new Error('Certificate not found');
      }

      // Only the issuer can delete the certificate
      if (requestingUserId && certificate.issuedBy !== requestingUserId) {
        throw new Error('Only the certificate issuer can delete this certificate');
      }

      await this.certificateRepository.deleteCertificate(certificateId);
      console.log('[CertificateUseCase.deleteCertificate] SUCCESS - Certificate deleted');
    } catch (error) {
      console.error('[CertificateUseCase.deleteCertificate] ERROR:', error);
      throw error;
    }
  }

  // Verify certificate authenticity
  async verifyCertificate(certificateNumber: string): Promise<{
    isValid: boolean;
    certificate?: GetCertificateData;
    reason?: string;
  }> {
    console.log('[CertificateUseCase.verifyCertificate] START - CertificateNumber:', certificateNumber);
    try {
      const certificate = await this.certificateRepository.verifyCertificate(certificateNumber);
      
      if (!certificate) {
        console.log('[CertificateUseCase.verifyCertificate] Certificate not found - invalid');
        return {
          isValid: false,
          reason: 'Certificate not found'
        };
      }

      // Check if certificate is expired
      if (certificate.expiryDate && certificate.expiryDate.toDate() <= new Date()) {
        console.log('[CertificateUseCase.verifyCertificate] Certificate expired - invalid');
        return {
          isValid: false,
          certificate,
          reason: 'Certificate has expired'
        };
      }

      console.log('[CertificateUseCase.verifyCertificate] SUCCESS - Certificate is valid');
      return {
        isValid: true,
        certificate
      };
    } catch (error) {
      console.error('[CertificateUseCase.verifyCertificate] ERROR:', error);
      throw error;
    }
  }

  // Get user certificates with business logic
  async getUserCertificates(userId: string, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateUseCase.getUserCertificates] START - UserId:', userId);
    try {
      // Access control - users can only see their own certificates
      if (requestingUserId && userId !== requestingUserId) {
        console.log('[CertificateUseCase.getUserCertificates] Access denied - not the same user');
        throw new Error('Access denied');
      }

      const certificates = await this.certificateRepository.getUserCertificates(userId, limitCount);
      console.log('[CertificateUseCase.getUserCertificates] SUCCESS - Retrieved', certificates.length, 'certificates for user');
      return certificates;
    } catch (error) {
      console.error('[CertificateUseCase.getUserCertificates] ERROR:', error);
      throw error;
    }
  }

  // Get certificates by issuer with validation
  async getCertificatesByIssuer(issuerId: string, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateUseCase.getCertificatesByIssuer] START - IssuerId:', issuerId);
    try {
      // Access control - users can only see certificates they issued
      if (requestingUserId && issuerId !== requestingUserId) {
        console.log('[CertificateUseCase.getCertificatesByIssuer] Access denied - not the issuer');
        throw new Error('Access denied - you can only view certificates you issued');
      }

      const certificates = await this.certificateRepository.getCertificatesByIssuer(issuerId, limitCount);
      console.log('[CertificateUseCase.getCertificatesByIssuer] SUCCESS - Retrieved', certificates.length, 'certificates for issuer');
      return certificates;
    } catch (error) {
      console.error('[CertificateUseCase.getCertificatesByIssuer] ERROR:', error);
      throw error;
    }
  }

  // Get expiring certificates
  async getExpiringCertificates(daysAhead: number = 30, requestingUserId?: string): Promise<GetCertificateData[]> {
    console.log('[CertificateUseCase.getExpiringCertificates] START - DaysAhead:', daysAhead);
    try {
      const expiringCertificates = await this.certificateRepository.getExpiringCertificates(daysAhead);
      
      // Filter by access control - users can only see their own expiring certificates or ones they issued
      const filteredCertificates = requestingUserId 
        ? expiringCertificates.filter(cert => 
            cert.userId === requestingUserId || cert.issuedBy === requestingUserId
          )
        : expiringCertificates;

      console.log('[CertificateUseCase.getExpiringCertificates] SUCCESS - Retrieved', filteredCertificates.length, 'expiring certificates');
      return filteredCertificates;
    } catch (error) {
      console.error('[CertificateUseCase.getExpiringCertificates] ERROR:', error);
      throw error;
    }
  }

  // Generate certificate from course completion
  async generateCertificateFromCompletion(
    userId: string, 
    courseId: string, 
    issuerId: string,
    completionData: {
      scorePercentage?: number;
      grade?: string;
      completionStatus: 'completed' | 'passed' | 'distinction';
      skillsEarned?: string[];
    }
  ): Promise<GetCertificateData> {
    console.log('[CertificateUseCase.generateCertificateFromCompletion] START - Generating certificate from completion');
    try {
      // Generate unique certificate number
      const certificateNumber = `CERT-${Date.now()}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
      
      // Create certificate data
      const certificateData: CreateCertificateData = {
        userId: userId,
        courseId: courseId,
        issuedBy: issuerId,
        issuedAt: new Date(),
        certificateNumber: certificateNumber,
        scorePercentage: completionData.scorePercentage,
        grade: completionData.grade,
        completionStatus: completionData.completionStatus,
        skillsEarned: completionData.skillsEarned,
        notes: `Certificate generated from course completion on ${new Date().toLocaleDateString()}`
      };

      console.log('[CertificateUseCase.generateCertificateFromCompletion] Creating certificate with data:', certificateData);
      const certificate = await this.createCertificate(certificateData);
      console.log('[CertificateUseCase.generateCertificateFromCompletion] SUCCESS - Generated certificate:', certificate.certificateNumber);
      return certificate;
    } catch (error) {
      console.error('[CertificateUseCase.generateCertificateFromCompletion] ERROR:', error);
      throw error;
    }
  }

  // Get certificate analytics
  async getCertificateAnalytics(issuerId?: string): Promise<{
    totalCertificates: number;
    completedCount: number;
    passedCount: number;
    distinctionCount: number;
    recentCertificates: GetCertificateData[];
    expiringThisMonth: number;
  }> {
    console.log('[CertificateUseCase.getCertificateAnalytics] START - Generating analytics');
    try {
      const filter: CertificateFilter = issuerId ? { issuedBy: issuerId } : {};
      const certificates = await this.certificateRepository.getCertificates(filter);
      
      const completedCount = certificates.filter(cert => cert.completionStatus === 'completed').length;
      const passedCount = certificates.filter(cert => cert.completionStatus === 'passed').length;
      const distinctionCount = certificates.filter(cert => cert.completionStatus === 'distinction').length;
      
      // Get recent certificates (last 10)
      const recentCertificates = certificates
        .sort((a, b) => b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime())
        .slice(0, 10);
      
      // Get certificates expiring this month
      const expiringThisMonth = await this.certificateRepository.getExpiringCertificates(30);
      const filteredExpiringThisMonth = issuerId 
        ? expiringThisMonth.filter(cert => cert.issuedBy === issuerId)
        : expiringThisMonth;

      const analytics = {
        totalCertificates: certificates.length,
        completedCount,
        passedCount,
        distinctionCount,
        recentCertificates,
        expiringThisMonth: filteredExpiringThisMonth.length
      };

      console.log('[CertificateUseCase.getCertificateAnalytics] SUCCESS - Generated analytics:', analytics);
      return analytics;
    } catch (error) {
      console.error('[CertificateUseCase.getCertificateAnalytics] ERROR:', error);
      throw error;
    }
  }
}